import { Fragment, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Row, Col, Form, Dropdown, Button } from "react-bootstrap";
import { FaCog, FaChevronDown } from "react-icons/fa";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import DateRangeFilter from "../components/DateRangeFilter";
import useFetchData from "../components/useFetchDatav2";
import SIDES, { renderCell, toReconcilePanelRowData } from "./invoicesSides";
import { usePreferences, useMoneyFormat, useDateFormat } from "../Context/PreferencesContext";
import { shouldAccentNegative } from "../utils/formatters";
import { useSavedView } from "../hooks/useSavedView";
import { DATE_MODES, resolveDateMode } from "../utils/dateModes";
import StatusPill from "../components/invoices/StatusPill";
import SummaryStrip from "../components/invoices/SummaryStrip";
import SideToggle from "../components/invoices/SideToggle";
import PdfModal from "../components/PdfModal";
import ReconcileLineDetailPanel from "../components/ReconcileLineDetailPanel";
import { convertBlobToBase64 } from "../utils/blob";

// Page default view. Dates are stored as an intent (dateMode) and re-resolved against
// today on load, so a saved "Last 3 months" is never stale. See useSavedView / dateModes.
const DEFAULT_DATE_MODE = "last_3_months";
const DEFAULT_VIEW = {
   side: "statement",
   status: "attention",
   pageSize: 20,
   colVis: { statement: {}, erp: {} },
   dateMode: DEFAULT_DATE_MODE,
};

const STATUS_OPTIONS = [
   { value: "attention", label: "Needs attention" },
   { value: "unmatched", label: "Unmatched" },
   { value: "partial", label: "Partial" },
   { value: "matched", label: "Matched" },
   { value: "all", label: "All" },
];

// Fixed column widths (table-layout: fixed) so switching sides/filters doesn't reflow the
// table. Text columns (supplier, invoice no) get no width and share the remaining space.
const COLW = { status: "84px", money: "120px", date: "120px", text: undefined };

// Column-settings gear — mirrors Documents' TableSettingDropdown. Temp state + Apply/Cancel.
function InvoicesTableSettings({ columns, visibility, pageSize, onApply }) {
   const [show, setShow] = useState(false);
   const [tempVis, setTempVis] = useState(visibility);
   const [tempSize, setTempSize] = useState(pageSize);
   const open = (isOpen) => {
      if (isOpen) { setTempVis(visibility); setTempSize(pageSize); }
      setShow(isOpen);
   };
   return (
      <Dropdown show={show} onToggle={open}>
         <Dropdown.Toggle as="span" variant="link" id="dropdown-table-columns" style={{ cursor: "pointer" }}>
            <FaCog />
         </Dropdown.Toggle>
         <Dropdown.Menu className="dropdown-table-columns-width" onClick={(e) => e.stopPropagation()}>
            <Dropdown.Header>Rows</Dropdown.Header>
            {[10, 20, 50].map((n) => (
               <div className="dropdown-item" key={n}>
                  <Form.Check type="checkbox" label={String(n)} checked={tempSize === n}
                     onChange={(e) => { e.stopPropagation(); setTempSize(n); }} />
               </div>
            ))}
            <Dropdown.Divider />
            <Dropdown.Header>View Columns</Dropdown.Header>
            {columns.map((c) => (
               <div className="dropdown-item" key={c.key}>
                  <div className="form-check">
                     <input type="checkbox" className="form-check-input"
                        id={`column-visibility-${c.key}`}
                        checked={tempVis[c.key] ?? true}
                        onChange={(e) => { e.stopPropagation(); setTempVis((v) => ({ ...v, [c.key]: !(v[c.key] ?? true) })); }} />
                     <label className="form-check-label" htmlFor={`column-visibility-${c.key}`}>{c.label}</label>
                  </div>
               </div>
            ))}
            <Dropdown.Divider />
            <div className="d-flex justify-content-center p-2">
               <Button variant="secondary" size="sm" onClick={() => setShow(false)}>Cancel</Button>
               <Button variant="primary" size="sm" className="ms-2"
                  onClick={() => { onApply({ visibility: tempVis, pageSize: tempSize }); setShow(false); }}>Apply</Button>
            </div>
         </Dropdown.Menu>
      </Dropdown>
   );
}

export default function Invoices() {
   const navigate = useNavigate();
   const { fetchData } = useFetchData();
   const { prefs } = usePreferences();
   const fmtMoney = useMoneyFormat();
   const fmtDate = useDateFormat();

   const [side, setSide] = useState(DEFAULT_VIEW.side);
   const [dateMode, setDateMode] = useState(DEFAULT_DATE_MODE);
   const [range, setRange] = useState(() => resolveDateMode(DEFAULT_DATE_MODE));
   const [status, setStatus] = useState(DEFAULT_VIEW.status);
   const [invoiceInput, setInvoiceInput] = useState(""); // controlled text box (responsive, session-only)
   const [invoiceNo, setInvoiceNo] = useState(""); // debounced query value used in load/buildBody
   const [pageIndex, setPageIndex] = useState(0);
   const [pageSize, setPageSize] = useState(DEFAULT_VIEW.pageSize);

   const [rows, setRows] = useState([]);
   const [counts, setCounts] = useState(null);
   const [totalPages, setTotalPages] = useState(1);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(null);

   // Column visibility per side: { statement: {key:bool}, erp: {key:bool} }
   const [colVis, setColVis] = useState({ statement: {}, erp: {} });
   // Statement PDF viewer ({ imageData, name } | null) + single-open inline row expansion.
   const [pdf, setPdf] = useState(null);
   const [expandedKey, setExpandedKey] = useState(null);
   const [rowDetail, setRowDetail] = useState({}); // key -> { status, data, error }
   const tableCardRef = useRef(null); // for click-outside-to-close of the expanded row

   const cfg = SIDES[side];
   const sideVis = colVis[side];
   const visibleColumns = cfg.columns.filter((c) => sideVis[c.key] ?? true);

   // ── Saved view ("Save filters") ───────────────────────────────────────────
   // Durable filters + date intent; invoice-number search is session-only (not saved).
   const getCurrent = () => ({ side, status, pageSize, colVis, dateMode });
   const applyView = (v) => {
      setSide(v.side ?? DEFAULT_VIEW.side);
      setStatus(v.status ?? DEFAULT_VIEW.status);
      setPageSize(v.pageSize ?? DEFAULT_VIEW.pageSize);
      setColVis(v.colVis ?? DEFAULT_VIEW.colVis);
      const mode = v.dateMode ?? DEFAULT_DATE_MODE;
      setDateMode(mode);
      const r = resolveDateMode(mode);
      if (r) setRange(r);
   };
   const { isDirty, isCustomDate, hasSavedView, baselineDateMode, save, reset } = useSavedView("invoices", {
      getCurrent,
      applyView,
      defaultView: DEFAULT_VIEW,
   });

   // Quick-range select → set intent + resolve dates. Calendar picks set "custom".
   const onPickDateMode = (mode) => {
      setDateMode(mode);
      const r = resolveDateMode(mode);
      if (r) setRange(r);
   };
   const onPickCustomRange = (value) => {
      setRange(value);
      setDateMode("custom");
   };

   // Hold the latest fetchData in a ref so it need not be a dependency of `load`.
   // useFetchDatav2 returns a NEW fetchData reference every render (it is not memoized),
   // so depending on it directly would give `load` a new identity on every commit and
   // make the `[load]` effect re-run on every render (refetch storm).
   const fetchDataRef = useRef(fetchData);
   useEffect(() => {
      fetchDataRef.current = fetchData; // keep latest each render
   });

   const load = useCallback(async () => {
      setLoading(true);
      setError(null);
      const body = cfg.buildBody({
         from: range.startDate,
         to: range.endDate,
         status,
         sort: "attention",
         invoiceNo,
         pageIndex,
         pageSize,
      });
      const { responseData, fetchError } = await fetchDataRef.current(cfg.endpoint, "POST", body, "form-data");
      setLoading(false);
      if (fetchError || !responseData || responseData.st !== 1) {
         setError(responseData?.mg || "Unable to load invoices.");
         setRows([]);
         setCounts(null);
         setTotalPages(1);
         return;
      }
      setRows(Array.isArray(responseData.payload) ? responseData.payload : []);
      setCounts(responseData.counts || null);
      setTotalPages(responseData.total_pages || 1);
   }, [cfg, range, status, invoiceNo, pageIndex, pageSize]);

   useEffect(() => {
      load();
   }, [load]);

   // reset to first page + collapse any open row whenever a filter or side changes
   useEffect(() => {
      setPageIndex(0);
      setExpandedKey(null);
      setRowDetail({});
   }, [side, status, invoiceNo, range]);

   // Close the expanded row on any click outside the table card (mirrors the Reconcile page's
   // click-outside pattern). The single-open toggle still handles clicking another row.
   useEffect(() => {
      if (!expandedKey) return undefined;
      const handleClickOutside = (event) => {
         if (tableCardRef.current && !tableCardRef.current.contains(event.target)) {
            setExpandedKey(null);
         }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
   }, [expandedKey]);

   // Debounce the invoice-number search: the text box stays responsive via `invoiceInput`,
   // while the query value `invoiceNo` (used by load/buildBody) updates ~400ms after the
   // user pauses typing — avoids hammering the Postgres LIKE on every keystroke.
   const debouncedSetInvoiceNo = useMemo(() => debounce((v) => setInvoiceNo(v), 400), []);
   useEffect(() => {
      debouncedSetInvoiceNo(invoiceInput);
   }, [invoiceInput, debouncedSetInvoiceNo]);
   useEffect(() => () => debouncedSetInvoiceNo.cancel(), [debouncedSetInvoiceNo]);

   const applySettings = ({ visibility, pageSize: ps }) => {
      setColVis((prev) => ({ ...prev, [side]: visibility }));
      setPageSize(ps);
      setPageIndex(0);
   };

   // ── View action: open the statement document PDF ──────────────────────────
   const handleViewPdf = async (row) => {
      if (!row.doc_external_id) return;
      const fd = new FormData();
      fd.append("_doc_id", row.doc_external_id);
      const { responseData, fetchError } = await fetchDataRef.current("/getfile.sz", "POST", fd, "form-data", "blob");
      if (fetchError || !responseData) { setError("Unable to open the statement document."); return; }
      const imageData = await convertBlobToBase64(responseData);
      setPdf({ imageData, name: `${row.supplier ?? ""} — ${row.invoice_no ?? ""}` });
   };

   // ── ▾ menu: jump to Reconcile, deep-linked so the clicked line auto-expands ──
   const openReconcile = (row) => {
      if (!row.sup_state_external_id) return;
      const q = new URLSearchParams({ stmt: row.sup_state_external_id });
      if (row.sup_state_detail_external_id) q.set("line", row.sup_state_detail_external_id);
      if (row.esh_external_id) q.set("erp", row.esh_external_id);
      navigate(`/apps/reconcile?${q.toString()}`, {
         state: {
            selectedInvoiceStatement: row.sup_state_external_id,
            selectedInvoiceStatementSupplierName: row.supplier,
            selectedInvoiceStatementDate: row.statement_date,
         },
      });
   };

   // ── ▾ menu: download the source document ──────────────────────────────────
   const handleDownload = async (row) => {
      if (!row.doc_external_id) return;
      const fd = new FormData();
      fd.append("doc_id", row.doc_external_id);
      const { responseData } = await fetchDataRef.current("/getdownloadurl.sz", "POST", fd, "form-data");
      if (responseData?.st === 1 && responseData.signurl) window.open(responseData.signurl, "_blank");
      else setError("Unable to download the document.");
   };

   // ── Row-click: inline-expand the reused Reconcile detail panel ─────────────
   const fetchRowDetail = async (key, row) => {
      setRowDetail((p) => ({ ...p, [key]: { status: "loading" } }));
      const fd = new FormData();
      fd.append("_sd_id", row.sup_state_detail_external_id ?? "");
      fd.append("_en_id", row.esh_external_id ?? "");
      const { responseData } = await fetchDataRef.current("/getInvStateDetails.sz", "POST", fd, "form-data");
      const data = responseData?.payload;
      const stmt = data?.statement_detail;
      const erp = data?.erp_detail;
      // Loaded if EITHER side came back (unmatched statement lines have statement_detail only —
      // we still want their scanned-confidence badges).
      const hasContent = (stmt && typeof stmt === "object") || (erp && typeof erp === "object");
      if (responseData?.st === 1 && hasContent) {
         setRowDetail((p) => ({ ...p, [key]: { status: "loaded", data } }));
      } else {
         setRowDetail((p) => ({ ...p, [key]: { status: "error", error: responseData?.mg || "" } }));
      }
   };

   const toggleExpand = (row) => {
      const key = cfg.rowKey(row);
      const isOpen = expandedKey === key;
      setExpandedKey(isOpen ? null : key);
      if (isOpen) return;
      if (rowDetail[key]) return;
      // Fetch whenever there's a statement line or an ERP bill to describe. Unmatched statement
      // lines (sd but no esh) still fetch so their scanned-confidence badges render.
      if (!row.sup_state_detail_external_id && !row.esh_external_id) {
         setRowDetail((p) => ({ ...p, [key]: { status: "empty" } }));
         return;
      }
      fetchRowDetail(key, row);
   };

   return (
      <>
         <Header />
         <div className="main main-app p-3 p-lg-4 p-xxl-5">
            <Row className="g-3 g-lg-4">
               <Col>
                  <h2 className="main-title fs-4 mb-1">Invoices</h2>
                  <p className="text-secondary mb-3">Statement and ERP invoices in one view — unmatched and partial first.</p>
               </Col>
            </Row>

            <Row className="align-items-end g-3 mb-2">
               <Col xs="auto">
                  <SideToggle side={side} onChange={setSide} />
               </Col>
               <Col xs="auto">
                  <Form.Label className="d-block mb-1">Status</Form.Label>
                  <Form.Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 180 }}>
                     {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                           {o.label}
                        </option>
                     ))}
                  </Form.Select>
               </Col>
               <Col xs="auto">
                  <Form.Label className="d-block mb-1">Date range</Form.Label>
                  <Form.Select value={dateMode} onChange={(e) => onPickDateMode(e.target.value)} style={{ width: 160 }}>
                     {DATE_MODES.map((m) => (
                        <option key={m.value} value={m.value}>
                           {m.label}
                        </option>
                     ))}
                  </Form.Select>
               </Col>
               <Col xs="auto">
                  <Form.Label className="d-block mb-1">Invoice date</Form.Label>
                  <DateRangeFilter
                     column={{ id: "inv_date", value: range }}
                     handleInputFilterChangeNoDebounce={(_id, value) => onPickCustomRange(value)}
                  />
               </Col>
               <Col xs="auto">
                  <Form.Label className="d-block mb-1">Invoice number</Form.Label>
                  <Form.Control
                     value={invoiceInput}
                     placeholder="Search invoice no."
                     onChange={(e) => setInvoiceInput(e.target.value)}
                     style={{ width: 200 }}
                  />
               </Col>
               <Col xs="auto" className="ms-auto d-flex align-items-end gap-2">
                  {isDirty && (
                     <Button size="sm" variant="primary" onClick={save}>
                        Save filters
                     </Button>
                  )}
                  {(hasSavedView || isDirty) && (
                     <Button size="sm" variant="outline-secondary" onClick={reset}>
                        Reset to default
                     </Button>
                  )}
               </Col>
            </Row>

            {isCustomDate && (
               <div className="text-secondary mb-2" style={{ fontSize: 13 }}>
                  Custom date range active —{" "}
                  <a
                     href="#"
                     onClick={(e) => {
                        e.preventDefault();
                        onPickDateMode(baselineDateMode);
                     }}>
                     reset date range
                  </a>
               </div>
            )}

            <SummaryStrip counts={counts} />

            {error && <div className="alert alert-danger">{error}</div>}

            <Row className="g-3 g-lg-4">
               <Col>
                  <div className="data-table-card" ref={tableCardRef}>
                     <table className="table" style={{ tableLayout: "fixed", width: "100%" }}>
                        <thead>
                           <tr>
                              {visibleColumns.map((c) => (
                                 <th key={c.key} style={{ width: COLW[c.kind] }}>
                                    {c.label}
                                 </th>
                              ))}
                              <th style={{ width: "120px", textAlign: "right" }}>
                                 <span className="d-inline-flex align-items-center justify-content-end gap-4 w-100">
                                    <InvoicesTableSettings columns={cfg.columns} visibility={sideVis}
                                       pageSize={pageSize} onApply={applySettings} />
                                 </span>
                              </th>
                           </tr>
                        </thead>
                        {/* Keep prior rows visible (dimmed) during a refetch so toggling sides/filters
                            doesn't collapse the table height and shift the page (scrollbar) — only show
                            the single-line message on a genuinely empty result. */}
                        {/* key={side} forces React to discard the whole tbody subtree when the
                            side toggles. Without it, swapping the entire row-identity space AND the
                            column structure at once made the keyed-list reconciliation orphan the old
                            rows (they accumulated in the DOM with stale detail panels). A clean
                            remount per side eliminates that at the root. */}
                        <tbody key={side} style={{ opacity: loading && rows.length ? 0.5 : 1, transition: "opacity 0.12s ease" }}>
                           {rows.length === 0 ? (
                              <tr>
                                 <td colSpan={visibleColumns.length + 1} className="text-secondary">
                                    {loading ? "Loading…" : "No invoices match your filters."}
                                 </td>
                              </tr>
                           ) : (
                              rows.map((row) => {
                                 const key = cfg.rowKey(row);
                                 const total = visibleColumns.length + 1;
                                 // Active side always shows its own pane; the counterpart pane shows
                                 // only when the line is actually matched (so no empty opposite pane).
                                 const hasStmt = !!row.sup_state_detail_external_id;
                                 const hasErp = !!row.esh_external_id;
                                 const showStatement = side === "statement" || hasStmt;
                                 const showErp = side === "erp" || hasErp;
                                 return (
                                    <Fragment key={key}>
                                       <tr style={{ cursor: "pointer" }} onClick={() => toggleExpand(row)}>
                                          {visibleColumns.map((c) => (
                                             <td
                                                key={c.key}
                                                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {c.kind === "status" ? (
                                                   <StatusPill status={row[c.key]} />
                                                ) : c.kind === "money" ? (
                                                   <span
                                                      style={
                                                         shouldAccentNegative(row[c.key], prefs.money)
                                                            ? { color: "#dc3545" }
                                                            : undefined
                                                      }>
                                                      {fmtMoney(row[c.key])}
                                                   </span>
                                                ) : (
                                                   renderCell(c.kind, row[c.key], { money: fmtMoney, date: fmtDate })
                                                )}
                                             </td>
                                          ))}
                                          <td onClick={(e) => e.stopPropagation()}>
                                             <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                                                {row.doc_external_id ? (
                                                   <a href="#" onClick={(e) => { e.preventDefault(); handleViewPdf(row); }}>View</a>
                                                ) : (
                                                   <span className="text-secondary">View</span>
                                                )}
                                                <div style={{ borderLeft: "1px solid #ccc", height: "16px" }} />
                                                <div className="dropdown">
                                                   <button className="btn btn-link p-0 border-0" type="button"
                                                      id={`invoices-rowmenu-${key}`} data-bs-toggle="dropdown"
                                                      aria-expanded="false" style={{ cursor: "pointer" }}>
                                                      <FaChevronDown />
                                                   </button>
                                                   <ul className="dropdown-menu" aria-labelledby={`invoices-rowmenu-${key}`}>
                                                      <li>
                                                         <button className="dropdown-item" disabled={!row.sup_state_external_id}
                                                            onClick={() => openReconcile(row)}>Open in Reconcile</button>
                                                      </li>
                                                      <li>
                                                         <button className="dropdown-item" disabled={!row.doc_external_id}
                                                            onClick={() => handleDownload(row)}>Download</button>
                                                      </li>
                                                   </ul>
                                                </div>
                                             </div>
                                          </td>
                                       </tr>
                                       {expandedKey === key && (
                                          <ReconcileLineDetailPanel
                                             rowData={toReconcilePanelRowData(row)}
                                             detail={rowDetail[key]}
                                             onRetry={() => fetchRowDetail(key, row)}
                                             showStatement={showStatement}
                                             showErp={showErp}
                                             totalCols={total}
                                          />
                                       )}
                                    </Fragment>
                                 );
                              })
                           )}
                        </tbody>
                     </table>
                  </div>
               </Col>
            </Row>

            {rows.length > 0 && (
               <Row className="g-3 g-lg-4">
                  <Col>
                     <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex justify-content-center flex-grow-1">
                           <span>
                              Page <strong>{pageIndex + 1}</strong> of {totalPages || 1}
                           </span>
                        </div>
                        <div className="d-flex justify-content-end">
                           <button
                              className="btn btn-primary me-2"
                              onClick={() => setPageIndex((p) => Math.max(p - 1, 0))}
                              disabled={pageIndex === 0}>
                              Previous
                           </button>
                           <button
                              className="btn btn-primary"
                              onClick={() => setPageIndex((p) => Math.min(p + 1, totalPages - 1))}
                              disabled={pageIndex >= totalPages - 1}>
                              Next
                           </button>
                        </div>
                     </div>
                  </Col>
               </Row>
            )}

            {pdf && (
               <PdfModal imageData={pdf.imageData} propScale={1.0}
                  docObj={{ name: pdf.name }} onCloseModal={() => setPdf(null)} />
            )}
            <Footer />
         </div>
      </>
   );
}
